import { useEffect } from 'react';
import Navbar from '../../components/feature/Navbar';
import Footer from '../../components/feature/Footer';
import WhatsAppButton from '../../components/feature/WhatsAppButton';
import ScrollToTopButton from '../../components/feature/ScrollToTopButton';
import { motion } from 'framer-motion';

const SCHEMA_INSTITUCIONAL = JSON.stringify({
  '@context': 'https://schema.org',
  '@graph': [
    {
      '@type': 'AboutPage',
      '@id': `${import.meta.env.VITE_SITE_URL}/institucional#webpage`,
      url: `${import.meta.env.VITE_SITE_URL}/institucional`,
      name: 'Sobre a Engeporto Engenharia | Missão, Visão e Valores',
      description: 'Conheça a Engeporto Engenharia: história, missão, visão, valores e o Eng. Rodrigo Naldi (CREA-SP 5071305786). Mais de 10 anos em Sorocaba e Campinas.',
      inLanguage: 'pt-BR',
      isPartOf: { '@id': `${import.meta.env.VITE_SITE_URL}/#website` },
    },
    {
      '@type': 'Person',
      name: 'Rodrigo Naldi',
      jobTitle: 'Engenheiro Civil e Engenheiro de Segurança do Trabalho',
      identifier: 'CREA-SP 5071305786',
      worksFor: {
        '@type': 'Organization',
        name: 'Engeporto Engenharia e Serviços Ltda.',
        url: import.meta.env.VITE_SITE_URL,
      },
      areaServed: ['Sorocaba', 'Campinas', 'Porto Feliz'],
    },
  ],
});

const InstitucionalPage = () => {
  useEffect(() => {
    document.title = 'Sobre a Engeporto Engenharia Sorocaba | Missão, Visão e Eng. Rodrigo Naldi';
    const setMeta = (name: string, content: string, prop = false) => {
      const sel = prop ? `meta[property="${name}"]` : `meta[name="${name}"]`;
      const el = document.querySelector(sel) as HTMLMetaElement | null;
      if (el) el.setAttribute('content', content);
    };
    setMeta('description', 'Conheça a Engeporto Engenharia: mais de 10 anos de experiência em Prevenção de Incêndio, Segurança do Trabalho e Perícias Técnicas nas Regiões Metropolitanas de Sorocaba e Campinas. Eng. Rodrigo Naldi — CREA-SP 5071305786.');
    setMeta('keywords', 'Engeporto Engenharia institucional, Rodrigo Naldi CREA-SP, engenharia Sorocaba, engenharia Campinas, missão visão valores');
    setMeta('og:title', 'Sobre a Engeporto Engenharia | Missão, Visão e Eng. Rodrigo Naldi', true);
    setMeta('og:description', 'Mais de 10 anos em Prevenção de Incêndio, Segurança do Trabalho e Perícias Técnicas em Sorocaba e Campinas.', true);
    setMeta('og:url', `${import.meta.env.VITE_SITE_URL}/institucional`, true);

    // Canonical
    let canonical = document.querySelector('link[rel="canonical"]') as HTMLLinkElement | null;
    if (!canonical) {
      canonical = document.createElement('link');
      canonical.setAttribute('rel', 'canonical');
      document.head.appendChild(canonical);
    }
    canonical.setAttribute('href', `${import.meta.env.VITE_SITE_URL}/institucional`);
    return () => { canonical?.remove(); };
  }, []);

  const valores = [
    {
      icon: 'ri-shield-check-line',
      title: 'Excelência Técnica',
      description: 'Compromisso com a qualidade e precisão em todos os projetos e serviços prestados.',
    },
    {
      icon: 'ri-user-heart-line',
      title: 'Ética Profissional',
      description: 'Transparência, honestidade e responsabilidade em todas as relações comerciais.',
    },
    {
      icon: 'ri-lightbulb-line',
      title: 'Inovação Contínua',
      description: 'Atualização constante com as melhores práticas e tecnologias do mercado.',
    },
    {
      icon: 'ri-customer-service-2-line',
      title: 'Foco no Cliente',
      description: 'Atendimento personalizado e soluções customizadas para cada necessidade.',
    },
  ];

  return (
    <div className="min-h-screen bg-white">
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: SCHEMA_INSTITUCIONAL }}
      />
      <Navbar />
      
      <section className="pt-36 pb-12 lg:pt-32 lg:pb-20 bg-[#0A1628] relative overflow-hidden">
        <div className="absolute inset-0 opacity-5">
          <div className="absolute inset-0" style={{
            backgroundImage: 'radial-gradient(circle, #D4AF37 1px, transparent 1px)',
            backgroundSize: '30px 30px'
          }}></div>
        </div>
        <div className="max-w-7xl mx-auto px-6 lg:px-8 relative z-10">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="text-center"
          >
            <h1 className="font-display font-bold text-3xl lg:text-5xl xl:text-6xl text-white mb-4 lg:mb-6">
              Sobre a Engeporto
            </h1>
            <p className="text-sm lg:text-xl text-neutral-300 max-w-3xl mx-auto">
              Autoridade técnica em engenharia especializada com mais de 10 anos de experiência nas Regiões Metropolitanas de Sorocaba e Campinas
            </p>
          </motion.div>
        </div>
      </section>

      <section className="py-12 lg:py-24 bg-white">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 lg:gap-16 items-center mb-12 lg:mb-24">
            <motion.div
              initial={{ opacity: 0, x: -50 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
            >
              <div className="inline-block bg-[#D4AF37]/10 text-[#D4AF37] px-4 py-2 rounded-full text-sm font-semibold mb-4 lg:mb-6">
                Nossa História
              </div>
              <h2 className="font-display font-bold text-2xl lg:text-4xl text-[#0A1628] mb-4 lg:mb-6">
                Construindo Segurança e Confiança desde 2015
              </h2>
              <div className="space-y-3 lg:space-y-4 text-sm lg:text-lg text-neutral-700 leading-relaxed">
                <p>
                  A <strong className="text-[#0A1628]">Engeporto Engenharia e Serviços Ltda.</strong> foi fundada com o propósito de oferecer soluções técnicas especializadas de alto padrão nas áreas de Prevenção de Incêndio, Segurança do Trabalho e Perícias Técnicas.
                </p>
                <p>
                  Ao longo de mais de 10 anos de atuação, consolidamos nossa presença nas Regiões Metropolitanas de Sorocaba e Campinas, atendendo empresas de diversos segmentos com excelência técnica e comprometimento.
                </p>
                <p>
                  Nossa equipe é formada por engenheiros altamente qualificados, com especializações nas principais normas técnicas brasileiras e experiência comprovada em projetos complexos.
                </p>
              </div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, x: 50 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
              className="relative"
            >
              <div className="aspect-[4/3] rounded-3xl overflow-hidden shadow-2xl">
                <img
                  src="https://public.readdy.ai/ai/img_res/598c56b8ae74a69b710bbd8b31542f25.jpg"
                  alt="Escritório Engeporto"
                  className="w-full h-full object-cover object-top"
                />
              </div>
              <div className="absolute -bottom-4 -right-4 lg:-bottom-8 lg:-right-8 bg-[#D4AF37] text-[#0A1628] px-5 py-4 lg:px-8 lg:py-6 rounded-2xl shadow-xl">
                <div className="font-display font-bold text-2xl lg:text-4xl mb-1">10+</div>
                <div className="text-xs lg:text-sm font-semibold">Anos de Experiência</div>
              </div>
            </motion.div>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 lg:gap-16 items-center">
            <motion.div
              initial={{ opacity: 0, x: -50 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
              className="order-2 lg:order-1"
            >
              <div className="aspect-[4/3] rounded-3xl overflow-hidden shadow-2xl">
                <img
                  src="https://static.readdy.ai/image/c4e06bad978995a8efa8373b87e2590a/5548774d200cd1690549a0781edd4d28.png"
                  alt="Engenheiro Responsável"
                  className="w-full h-full object-cover object-top"
                />
              </div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, x: 50 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
              className="order-1 lg:order-2"
            >
              <div className="inline-block bg-[#D4AF37]/10 text-[#D4AF37] px-4 py-2 rounded-full text-sm font-semibold mb-4 lg:mb-6">
                Liderança Técnica
              </div>
              <h2 className="font-display font-bold text-2xl lg:text-4xl text-[#0A1628] mb-4 lg:mb-6">
                Eng. Rodrigo Naldi
              </h2>
              <p className="text-[#D4AF37] font-semibold text-sm lg:text-base mb-4 lg:mb-6">
                CREA-SP 5071305786 | Eng. Civil e Eng. de Segurança do Trabalho
              </p>
              <div className="space-y-3 lg:space-y-4 text-sm lg:text-lg text-neutral-700 leading-relaxed">
                <p>
                  Fundador e Diretor Técnico da Engeporto, com mais de 10 anos de experiência em engenharia especializada.
                </p>
                <p>
                  Atuação especializada em Engenharia de Prevenção de Incêndio e Segurança do Trabalho, com expertise em Avaliações e Perícias. Alia o rigor técnico à Gestão de Riscos e Compliance para garantir conformidade e proteção patrimonial.
                </p>
                <p>
                  Responsável técnico por centenas de projetos de AVCB, CLCB, PGR e laudos especializados nas regiões de Sorocaba e Campinas.
                </p>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      <section className="py-12 lg:py-24 bg-neutral-50">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="text-center mb-10 lg:mb-16"
          >
            <h2 className="font-display font-bold text-2xl lg:text-4xl xl:text-5xl text-[#0A1628] mb-4">
              Missão, Visão e Valores
            </h2>
          </motion.div>

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 lg:gap-8 mb-10 lg:mb-16">
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.1, duration: 0.6 }}
              className="bg-white rounded-3xl p-6 lg:p-10 shadow-lg"
            >
              <div className="w-14 h-14 lg:w-16 lg:h-16 flex items-center justify-center bg-[#D4AF37]/10 rounded-2xl mb-4 lg:mb-6">
                <i className="ri-target-line text-2xl lg:text-3xl text-[#D4AF37]"></i>
              </div>
              <h3 className="font-display font-bold text-xl lg:text-2xl text-[#0A1628] mb-3 lg:mb-4">Missão</h3>
              <p className="text-sm lg:text-base text-neutral-700 leading-relaxed">
                Garantir segurança, conformidade e tranquilidade aos nossos clientes através de soluções técnicas de alto padrão, atendimento personalizado e comprometimento com a excelência.
              </p>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.2, duration: 0.6 }}
              className="bg-white rounded-3xl p-6 lg:p-10 shadow-lg"
            >
              <div className="w-14 h-14 lg:w-16 lg:h-16 flex items-center justify-center bg-[#D4AF37]/10 rounded-2xl mb-4 lg:mb-6">
                <i className="ri-eye-line text-2xl lg:text-3xl text-[#D4AF37]"></i>
              </div>
              <h3 className="font-display font-bold text-xl lg:text-2xl text-[#0A1628] mb-3 lg:mb-4">Visão</h3>
              <p className="text-sm lg:text-base text-neutral-700 leading-relaxed">
                Ser reconhecida como a principal referência em engenharia especializada nas Regiões Metropolitanas de Sorocaba e Campinas, destacando-se pela qualidade técnica e inovação.
              </p>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.3, duration: 0.6 }}
              className="bg-white rounded-3xl p-6 lg:p-10 shadow-lg"
            >
              <div className="w-14 h-14 lg:w-16 lg:h-16 flex items-center justify-center bg-[#D4AF37]/10 rounded-2xl mb-4 lg:mb-6">
                <i className="ri-heart-line text-2xl lg:text-3xl text-[#D4AF37]"></i>
              </div>
              <h3 className="font-display font-bold text-xl lg:text-2xl text-[#0A1628] mb-3 lg:mb-4">Valores</h3>
              <p className="text-sm lg:text-base text-neutral-700 leading-relaxed">
                Excelência técnica, ética profissional, inovação contínua, foco no cliente, responsabilidade social e comprometimento com resultados.
              </p>
            </motion.div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 lg:gap-6">
            {valores.map((valor, index) => (
              <motion.div
                key={valor.title}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1, duration: 0.5 }}
                className="bg-white rounded-2xl p-5 lg:p-6 shadow-sm hover:shadow-lg transition-all"
              >
                <div className="w-10 h-10 lg:w-12 lg:h-12 flex items-center justify-center bg-[#D4AF37]/10 rounded-xl mb-3 lg:mb-4">
                  <i className={`${valor.icon} text-xl lg:text-2xl text-[#D4AF37]`}></i>
                </div>
                <h4 className="font-display font-bold text-base lg:text-lg text-[#0A1628] mb-2">
                  {valor.title}
                </h4>
                <p className="text-xs lg:text-sm text-neutral-600 leading-relaxed">
                  {valor.description}
                </p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      <section className="py-12 lg:py-24 bg-[#0A1628] relative overflow-hidden">
        <div className="absolute inset-0 opacity-5">
          <div className="absolute inset-0" style={{
            backgroundImage: 'radial-gradient(circle, #D4AF37 1px, transparent 1px)',
            backgroundSize: '30px 30px'
          }}></div>
        </div>
        <div className="max-w-4xl mx-auto px-6 lg:px-8 text-center relative z-10">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
          >
            <h2 className="font-display font-bold text-2xl lg:text-4xl xl:text-5xl text-white mb-4 lg:mb-6">
              Pronto para Trabalhar Conosco?
            </h2>
            <p className="text-sm lg:text-xl text-neutral-300 mb-8 lg:mb-10">
              Entre em contato e descubra como podemos ajudar sua empresa
            </p>
            <a
              href={`https://wa.me/5515996719308?text=${encodeURIComponent('Olá! Vim pela página Institucional e gostaria de falar com um especialista.')}`}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 lg:gap-3 bg-[#D4AF37] text-[#0A1628] px-6 py-3 lg:px-10 lg:py-4 rounded-full text-sm lg:text-lg font-bold hover:bg-[#C4A137] transition-all shadow-xl whitespace-nowrap cursor-pointer"
            >
              Falar com Especialista
              <i className="ri-arrow-right-line text-lg lg:text-xl"></i>
            </a>
          </motion.div>
        </div>
      </section>

      <Footer />
      <WhatsAppButton />
      <ScrollToTopButton />
    </div>
  );
};

export default InstitucionalPage;